import {
  Activity,
  Gauge,
  MessageSquareWarning,
  PackageCheck,
  ScanSearch,
  Undo2,
  UserRound,
  Calculator,
  Coins,
  FileBarChart,
  FolderTree,
  Globe,
  Globe2,
  Gift,
  Handshake,
  Inbox,
  Landmark,
  LayoutDashboard,
  Mail,
  MapPinned,
  ScrollText,
  ShoppingCart,
  Tags,
  TicketPercent,
  BadgePercent,
  Building2,
  ChartLine,
  Megaphone,
  Truck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { Permission } from '@/shared/auth/permissions';

/**
 * The single source of truth for admin navigation.
 *
 * The sidebar, the command palette, the breadcrumb trail and the post-login
 * landing redirect all read from this array. Adding a module means adding one
 * entry here plus a route — nothing else duplicates the list (§20).
 *
 * Every item is gated by a permission from `/admin/me`; the matching route in
 * `app/router/routes.tsx` must require the same one, so a hidden nav item and a
 * blocked route stay consistent.
 *
 * PLANNED ITEMS are the modules not built yet, shown to Admin only as disabled
 * "Soon" entries so the roadmap stays visible. They have no route, are left out
 * of the command palette and the landing redirect, and are all gated by
 * `PLANNED_PREVIEW_PERMISSION` — an Admin-only permission — rather than by the
 * reporting permissions (reports.*, …), which other roles hold and which must
 * not create UI before their feature exists. When a module is built, its entry
 * becomes `status: 'ready'`, gets its real permission, and gains a route.
 */

export type NavItemStatus = 'ready' | 'planned';

export interface NavItem {
  title: string;
  path: string;
  icon: LucideIcon;
  /** Hidden entirely when the user lacks this permission (or every one of a list: any one opens it). */
  permission: Permission | readonly Permission[];
  status: NavItemStatus;
  /** Shown in the command palette to disambiguate similar entries. */
  keywords?: readonly string[];
  /** A live count shown next to the entry in the sidebar. */
  badge?: 'pendingApprovals' | 'lowBalanceProviders';
}

/**
 * Gate for planned (not yet built) items. `users.manage` is held only by Admin
 * (UT system/Authorization/RolePermissions.cs), so no other role sees the
 * roadmap entries. A preview gate only — it grants nothing.
 */
export const PLANNED_PREVIEW_PERMISSION: Permission = 'users.manage';

/** For a roadmap entry; none is listed today (unbuilt modules are hidden until they ship). */
export const planned = (item: Omit<NavItem, 'permission' | 'status'>): NavItem => ({
  ...item,
  permission: PLANNED_PREVIEW_PERMISSION,
  status: 'planned',
});

export interface NavSection {
  title: string;
  items: readonly NavItem[];
}

export const NAVIGATION: readonly NavSection[] = [
  {
    title: 'Overview',
    items: [
      {
        title: 'Dashboard',
        path: '/dashboard',
        icon: LayoutDashboard,
        permission: 'dashboard.read',
        status: 'ready',
        keywords: ['summary', 'metrics'],
      },
    ],
  },
  {
    title: 'Catalogue',
    items: [
      {
        title: 'Brands',
        path: '/brands',
        icon: Tags,
        permission: 'catalogue.read',
        status: 'ready',
        keywords: ['merchant', 'catalogue', 'apple', 'playstation'],
      },
      {
        title: 'Categories',
        path: '/categories',
        icon: FolderTree,
        permission: 'catalogue.read',
        status: 'ready',
        keywords: ['group', 'gaming', 'streaming', 'storefront'],
      },
      {
        title: 'Regions',
        path: '/regions',
        icon: Globe,
        permission: 'catalogue.read',
        status: 'ready',
        keywords: ['store', 'us', 'uae', 'global'],
      },
      {
        title: 'Country market',
        path: '/market',
        icon: MapPinned,
        permission: 'catalogue.read',
        status: 'ready',
        keywords: ['country', 'availability', 'offer', 'withdraw', 'sellable'],
      },
    ],
  },
  {
    title: 'Providers',
    items: [
      {
        // Mappings live on each provider and in Brand Details (one Admin API).
        title: 'Providers',
        path: '/providers',
        icon: Truck,
        permission: 'providers.read',
        status: 'ready',
        keywords: ['vendor', 'supplier', 'mintroute', 'likecard', 'balance', 'mapping'],
        badge: 'lowBalanceProviders',
      },
    ],
  },
  {
    title: 'Markets & Pricing',
    items: [
      {
        title: 'Countries',
        path: '/countries',
        icon: Globe2,
        permission: 'pricing.read',
        status: 'ready',
        keywords: ['market', 'pricing', 'margin', 'fx', 'exchange rate', 'payment channel', 'vat'],
      },
      {
        title: 'Currencies',
        path: '/currencies',
        icon: Coins,
        permission: 'pricing.read',
        status: 'ready',
        keywords: ['currency', 'money', 'symbol'],
      },
      {
        // Shows provider cost and margin: Admin, Finance, Catalogue Manager only.
        title: 'Pricing calculator',
        path: '/pricing/calculator',
        icon: Calculator,
        permission: 'pricing.calculation.read',
        status: 'ready',
        keywords: [
          'price',
          'preview',
          'inspector',
          'breakdown',
          'why',
          'margin',
          'provider cost',
          'checkout',
        ],
      },
    ],
  },
  {
    title: 'Operations',
    items: [
      {
        title: 'Orders',
        path: '/orders',
        icon: ShoppingCart,
        permission: 'orders.read',
        status: 'ready',
        keywords: ['purchase', 'sales', 'transactions'],
      },
      {
        title: 'Customers',
        path: '/customers',
        icon: UserRound,
        permission: 'customers.read',
        status: 'ready',
        keywords: ['buyer', 'account', 'phone'],
      },
      {
        title: 'Purchase limits',
        path: '/customers/limits',
        icon: Gauge,
        permission: 'customers.read',
        status: 'ready',
        keywords: ['limit', 'risk', 'fraud', 'spending cap', 'purchase control'],
      },
      {
        title: 'Complaints',
        path: '/complaints',
        icon: MessageSquareWarning,
        // complaints.read: the read-only, limited view (no role holds it). orders.details.read does
        // not open complaints — Finance holds it and sees no complaint.
        permission: ['complaints.manage', 'complaints.read'],
        status: 'ready',
        keywords: ['ticket', 'issue', 'support'],
      },
      {
        title: 'Refunds',
        path: '/refunds',
        icon: Undo2,
        permission: 'refunds.request',
        status: 'ready',
        keywords: ['money back', 'approval'],
      },
      {
        title: 'Reconciliation',
        path: '/reconciliation',
        icon: ScanSearch,
        permission: 'reconciliation.read',
        status: 'ready',
        keywords: ['mismatch', 'stuck', 'charged', 'failed'],
      },
      {
        title: 'Fulfilment',
        path: '/fulfilment',
        icon: PackageCheck,
        permission: 'reconciliation.read',
        status: 'ready',
        keywords: ['purchase', 'provider', 'repurchase', 'payment start', 'follow-up'],
      },
    ],
  },
  {
    title: 'Marketing',
    items: [
      {
        title: 'Newsletter',
        path: '/newsletter',
        icon: Mail,
        permission: 'newsletter.read',
        status: 'ready',
        keywords: ['subscribers', 'mailing list', 'email', 'unsubscribe', 'marketing'],
      },
      {
        title: 'Loyalty points',
        path: '/loyalty',
        icon: Gift,
        permission: 'loyalty.read',
        status: 'ready',
        keywords: [
          'points',
          'loyalty',
          'earning rules',
          'registration points',
          'rewards',
          'configuration',
          'points coupons',
          'points tiers',
        ],
      },
      {
        title: 'Coupons',
        path: '/coupons',
        icon: TicketPercent,
        permission: 'coupons.read',
        status: 'ready',
        keywords: ['coupon', 'discount', 'promo code', 'voucher code', 'percent off'],
      },
      {
        title: 'Global discount',
        path: '/global-discount',
        icon: BadgePercent,
        permission: 'coupons.read',
        status: 'ready',
        keywords: ['sale', 'global discount', 'banner', 'percent off everything', 'campaign'],
      },
      {
        title: 'Campaigns',
        path: '/campaigns',
        icon: Megaphone,
        permission: 'campaigns.read',
        status: 'ready',
        keywords: [
          'campaign',
          'tracker',
          'tracker link',
          'utm',
          'referral',
          'signups',
          'attribution',
          'ads',
          'influencer',
        ],
      },
      {
        // Counts and revenue per campaign, to send to an agency (never customers).
        title: 'Campaign report',
        path: '/campaigns/report',
        icon: ChartLine,
        permission: 'campaigns.read',
        status: 'ready',
        keywords: ['campaign', 'tracker', 'agency', 'utm', 'referral', 'export', 'csv'],
      },
      {
        title: 'Agencies',
        path: '/agencies',
        icon: Building2,
        permission: 'campaigns.read',
        status: 'ready',
        keywords: ['agency', 'marketing agency', 'campaign', 'tracker', 'utm', 'referral'],
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        title: 'Staff',
        path: '/staff',
        icon: Users,
        permission: 'users.manage',
        status: 'ready',
        keywords: ['users', 'accounts', 'roles', 'team'],
      },
    ],
  },
  {
    title: 'Insights',
    items: [
      {
        title: 'Reports',
        path: '/reports',
        icon: FileBarChart,
        permission: 'reports.operations.read',
        status: 'ready',
        keywords: ['report', 'revenue', 'margin', 'success rate', 'failures', 'refunds'],
      },
      {
        // The Partner's own report (RBAC role specification, Role 5): its share, no cost.
        title: 'Partner report',
        path: '/partner-report',
        icon: Handshake,
        permission: 'reports.partner.read',
        status: 'ready',
        keywords: ['partner', 'share', 'net share', 'aggregator'],
      },
      {
        // Channel statements (what a payment channel owes us) and partner
        // statements (the partner share we owe), with their payments.
        title: 'Settlements',
        path: '/settlements',
        icon: Landmark,
        permission: 'settlements.read',
        status: 'ready',
        keywords: [
          'settlement',
          'statement',
          'channel',
          'partner share',
          'payout',
          'outstanding',
          'bank transfer',
          'finance',
        ],
      },
      {
        title: 'Approvals',
        path: '/approvals',
        icon: Inbox,
        permission: 'approvals.read',
        status: 'ready',
        keywords: [
          'inbox',
          'four eyes',
          'approve',
          'reject',
          'waiting',
          'pricing version',
          'manual rate',
          'channel fees',
        ],
        badge: 'pendingApprovals',
      },
      {
        title: 'Audit Log',
        path: '/audit-log',
        icon: ScrollText,
        permission: 'reports.audit.read',
        status: 'ready',
        keywords: ['history', 'who changed', 'audit'],
      },
      {
        title: 'System health',
        path: '/system-health',
        icon: Activity,
        permission: 'system.health.read',
        status: 'ready',
        keywords: ['status', 'uptime', 'services', 'exchange rates', 'jobs', 'database'],
      },
    ],
  },
];

/** Flattened lookup, for breadcrumbs and the command palette. */
export const NAV_ITEMS: readonly NavItem[] = NAVIGATION.flatMap((section) => section.items);

export function findNavItemByPath(pathname: string): NavItem | undefined {
  // Longest match wins so /brands/123 resolves to the Brands entry.
  return [...NAV_ITEMS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));
}

/**
 * Where a user lands after sign-in or on `/`: the Dashboard when they may see
 * it, then the Partner report (the Partner's own screen), otherwise the first
 * screen they are permitted, in navigation order. Null when none is permitted.
 */
export function landingPath(can: (permission: Permission) => boolean): string | null {
  if (can('dashboard.read')) return '/dashboard';
  if (can('reports.partner.read')) return '/partner-report';
  return NAV_ITEMS.find((item) => item.status === 'ready' && canSee(item, can))?.path ?? null;
}

/** The permissions that open a nav item, as a list. */
export function navPermissions(item: NavItem): readonly Permission[] {
  return typeof item.permission === 'string' ? [item.permission] : item.permission;
}

/** Whether the user may see a nav item: any one of its permissions is enough. */
export function canSee(item: NavItem, can: (permission: Permission) => boolean): boolean {
  return navPermissions(item).some((permission) => can(permission));
}
